import { memo, useMemo } from "react";
import { Marker } from "@react-google-maps/api";
import { CurrentViewTypeEnum } from "../typings";
import { CustomFeature, CustomLatLng } from "core/features/maps/typings";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** Transparent 1×1 PNG — hides the pin while keeping the label visible. */
const TRANSPARENT_ICON_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

let transparentIconSize: google.maps.Size | undefined;
let transparentIconAnchor: google.maps.Point | undefined;
let transparentLabelOrigin: google.maps.Point | undefined;

function getTransparentIcon() {
  if (typeof google === "undefined") {
    return { url: TRANSPARENT_ICON_URL };
  }
  if (!transparentIconSize) {
    transparentIconSize = new google.maps.Size(1, 1);
    transparentIconAnchor = new google.maps.Point(0, 0);
    transparentLabelOrigin = new google.maps.Point(0, 0);
  }
  return {
    url: TRANSPARENT_ICON_URL,
    size: transparentIconSize,
    scaledSize: transparentIconSize,
    anchor: transparentIconAnchor,
    labelOrigin: transparentLabelOrigin,
  };
}

export function isEmptyFeatureId(id: unknown): boolean {
  if (id == null || id === "") {
    return true;
  }
  return String(id).toLowerCase() === EMPTY_GUID;
}

/** Stable unique key — never rely only on empty GUIDs or shared station codes. */
export function getTalhaoMarkerKey(
  item: {
    properties?: {
      id?: string;
      codigo?: string;
      pontoCentralLat?: number;
      pontoCentralLong?: number;
    } | null;
  },
  index: number
): string {
  const props = item.properties;
  const id = !isEmptyFeatureId(props?.id) ? String(props?.id) : "no-id";
  const codigo = props?.codigo != null && props.codigo !== ""
    ? String(props.codigo)
    : "no-codigo";
  const lat = props?.pontoCentralLat ?? "no-lat";
  const lng = props?.pontoCentralLong ?? "no-lng";

  // Prefix + index guarantee uniqueness among siblings (talhão vs estação).
  return `talhao:${id}:${codigo}:${lat}:${lng}:${index}`;
}

export interface TalhaoCodigoMarkerProps {
  item: {
    properties?: {
      id?: string;
      codigo?: string;
      pontoCentralLat?: number;
      pontoCentralLong?: number;
    } | null;
  };
  currentViewType: CurrentViewTypeEnum;
  displayMarkers: boolean;
  onClickItem?: (latLng: CustomLatLng, feature: CustomFeature) => void;
}

/**
 * Lightweight talhão label marker (same idea as ExecucaoTalhaoMarker).
 */
function TalhaoCodigoMarkerComponent({
  item,
  currentViewType,
  displayMarkers,
  onClickItem,
}: TalhaoCodigoMarkerProps) {
  const lat = item.properties?.pontoCentralLat;
  const lng = item.properties?.pontoCentralLong;
  const codigo = item.properties?.codigo ?? "";
  const isBox = currentViewType === CurrentViewTypeEnum.box;
  const showMarker =
    currentViewType !== CurrentViewTypeEnum.transparent &&
    displayMarkers &&
    !!codigo;

  const label = useMemo(
    () => ({
      text: codigo,
      color: "#000",
      fontWeight: isBox ? "regular" : "bold",
      fontSize: isBox ? "12px" : "11px",
    }),
    [codigo, isBox]
  );

  const icon = useMemo(() => {
    if (isBox) {
      return { url: "/marker.png" };
    }

    // numberOnly: plain black text, no white circle behind the code.
    return getTransparentIcon();
  }, [isBox]);

  const position = useMemo(
    () => ({
      lat: lat as number,
      lng: lng as number,
    }),
    [lat, lng]
  );

  if (lat == null || lng == null) {
    return null;
  }

  return (
    <Marker
      visible={showMarker}
      clickable={!!onClickItem}
      label={label}
      icon={icon}
      position={position}
      options={{ optimized: true }}
      onClick={(e) => {
        if (e.latLng && item.properties) {
          onClickItem?.(e.latLng, item.properties as CustomFeature);
        }
      }}
    />
  );
}

export const TalhaoCodigoMarker = memo(TalhaoCodigoMarkerComponent);
